import type { Schemas } from '#shopware'
import { ApiClientError } from '@shopware/api-client'

export function useClickAndReserve() {
    // Global state for click and reserve items using useState for SSR safety
    const clickAndReserveItems = useState<string[]>('clickAndReserveItems', () => [])
    const clickAndReserveProducts = useState<Schemas['Product'][]>('clickAndReserveProducts', () => [])
    const isLoadingProducts = useState<boolean>('isLoadingClickAndReserveProducts', () => false)

    const { apiClient } = useShopwareContext()
    const { resolveApiErrors } = useApiErrorsResolver()
    const { pushError } = useNotifications()

    // LocalStorage key
    const STORAGE_KEY = 'clickAndReserveItems'

    /**
     * Load items from localStorage (client-side only)
     */
    const loadFromStorage = (): void => {
        if (import.meta.client) {
            try {
                const stored = localStorage.getItem(STORAGE_KEY)
                if (stored) {
                    const items = JSON.parse(stored)
                    if (Array.isArray(items)) {
                        clickAndReserveItems.value = items
                    }
                }
            } catch (error) {
                console.error('Failed to load click and reserve items from localStorage:', error)
            }
        }
    }

    /**
     * Save items to localStorage (client-side only)
     */
    const saveToStorage = (): void => {
        if (import.meta.client) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(clickAndReserveItems.value))
            } catch (error) {
                console.error('Failed to save click and reserve items to localStorage:', error)
            }
        }
    }

    /**
     * Add product to click and reserve list
     */
    const addToClickAndReserve = (productId: string): void => {
        if (!clickAndReserveItems.value.includes(productId)) {
            clickAndReserveItems.value.push(productId)
            saveToStorage()
        }
    }

    /**
     * Remove product from click and reserve list
     */
    const removeFromClickAndReserve = (productId: string): void => {
        const index = clickAndReserveItems.value.indexOf(productId)
        if (index > -1) {
            clickAndReserveItems.value.splice(index, 1)
            saveToStorage()

            // Also remove from loaded products
            clickAndReserveProducts.value = clickAndReserveProducts.value.filter(
                product => product.id !== productId
            )
        }
    }

    /**
     * Clear all items from click and reserve list
     */
    const clearClickAndReserve = (): void => {
        clickAndReserveItems.value = []
        clickAndReserveProducts.value = []
        saveToStorage()
    }

    /**
     * Check if product is in click and reserve list
     */
    const isInClickAndReserve = (productId: string): boolean => {
        return clickAndReserveItems.value.includes(productId)
    }

    /**
     * Load detailed product information for all click and reserve items
     * Fetches product data with only necessary fields for display in ProductListItem
     */
    const loadClickAndReserveProducts = async (): Promise<void> => {
        if (clickAndReserveItems.value.length === 0) {
            clickAndReserveProducts.value = []
            return
        }

        isLoadingProducts.value = true
        try {
            const result = await apiClient.invoke('readProduct post /product', {
                body: {
                    filter: [
                        {
                            type: 'equalsAny',
                            field: 'id',
                            value: clickAndReserveItems.value.join('|')
                        }
                    ],
                    limit: clickAndReserveItems.value.length,
                    associations: {
                        media: {},
                        cover: { associations: { media: {} } },
                        options: { associations: { group: {} } },
                        properties: { associations: { group: {} } },
                        seoUrls: {}
                    },
                    includes: {
                        product: [
                            'id',
                            'name',
                            'translated',
                            'calculatedPrice',
                            'calculatedPrices',
                            'calculatedCheapestPrice',
                            'cover',
                            'options',
                            'properties',
                            'seoUrls'
                        ],
                        product_media: [
                            'id',
                            'media'
                        ],
                        media: [
                            'id',
                            'url',
                            'alt',
                            'title'
                        ],
                        property_group_option: [
                            'id',
                            'name',
                            'translated',
                            'group',
                            'groupId'
                        ],
                        property_group: [
                            'id',
                            'name',
                            'translated'
                        ],
                        seo_url: [
                            'seoPathInfo'
                        ]
                    }
                }
            })
            clickAndReserveProducts.value = result.data.elements || []
        } catch (error) {
            handleError(error)
            clickAndReserveProducts.value = []
        } finally {
            isLoadingProducts.value = false
        }
    }

    const handleError = (e: unknown) => {
        if (e instanceof ApiClientError) {
            const _errors = resolveApiErrors(e.details.errors)
            for (const error of _errors) {
                pushError(error)
            }
        }
    }

    // Load items from localStorage on initialization (client-side only)
    if (import.meta.client) {
        loadFromStorage()
    }

    return {
        clickAndReserveItems: computed(() => clickAndReserveItems.value),
        clickAndReserveProducts: computed(() => clickAndReserveProducts.value),
        isLoadingProducts: computed(() => isLoadingProducts.value),
        addToClickAndReserve,
        removeFromClickAndReserve,
        clearClickAndReserve,
        isInClickAndReserve,
        loadClickAndReserveProducts,
        count: computed(() => clickAndReserveItems.value.length)
    }
}
